/**
 * Nokta sorumluluğu raporu — KK-521, KK-534, AK-23, KK-608.
 *
 * =============================================================================
 * ZAMANINDA İADE, FİRE DEĞİLDİR — VE BU EKRANDA DA YAZAR
 * -----------------------------------------------------------------------------
 * Bu raporun amacı yalnızca "hangi nokta malı çarçur ediyor" değil, aynı
 * zamanda DÜRÜST DAVRANIŞI CEZALANDIRMAMAKTIR. SKT'si dolmadan malını depoya
 * iade eden görevli ile malı elinde bozan görevli aynı sütuna yazılırsa,
 * sistem görevliye "iade etme, sakla" der ve kendi verisini bozar.
 *
 * Bu yüzden `fire_*` ve `zamaninda_iade` AYRI SÜTUNLARDIR, TOPLANMAZLAR ve
 * ekran bunu bir açıklama satırıyla değil, tablonun başlığı ve üstündeki
 * kalıcı not ile söyler. İki sütunu tek göstergede birleştirmek AK-23 ihlalidir.
 *
 * GRANÜLERLİK: kaynak görünüm AY bazında gruplar; sunucu aralığı ait olduğu
 * aylara yuvarlar ve `ay_granulerligi` ile bildirir. Bu da ekranda yazılıdır —
 * sessizce farklı bir dönemi raporlamak rakamı sahiplenilemez yapar.
 * =============================================================================
 */
import { useEffect, useState } from "react";
import {
  Rozet,
  Tablo,
  TabloBaslik,
  TabloGovde,
  Tutar,
  Uyari,
} from "@kantin/ui";
import { yerelGun } from "@kantin/shared";
import { oturumDurumu } from "../../veri/oturum.js";
import { useNoktaSorumluluk, useOkul } from "../../veri/sorgular.js";
import { ayinIlkGunu, gunGoster, yerelBugun } from "../../veri/bicim.js";
import {
  DisaAktar,
  FiltreSeridi,
  Sayfa,
  SayfaBasligi,
  SorguDurumu,
  TarihAlani,
} from "../../ortak.js";

export function NoktaSorumluluk() {
  const seciliNoktaId = oturumDurumu((s) => s.seciliNoktaId);
  const okul = useOkul();
  const [bas, setBas] = useState(ayinIlkGunu(yerelBugun()));
  const [bit, setBit] = useState(yerelBugun());
  const [dokunuldu, setDokunuldu] = useState(false);

  useEffect(() => {
    if (dokunuldu || !okul.data) return;
    const gun = yerelGun(new Date(), okul.data.saat_dilimi);
    setBas(ayinIlkGunu(gun));
    setBit(gun);
  }, [okul.data, dokunuldu]);

  const rapor = useNoktaSorumluluk(bas, bit, seciliNoktaId);
  const satirlar = rapor.data?.noktalar ?? [];

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Nokta sorumluluğu"
        aciklama="Dönemsel fire, zamanında iade, sayım farkı, transfer farkı ve iptal oranı — noktalar karşılaştırmalı."
        eylem={
          <DisaAktar
            rapor="nokta-sorumluluk"
            dosyaKoku={`nokta-sorumluluk-${bas}_${bit}`}
            sorgu={{ bas, bit, nokta_id: seciliNoktaId ?? undefined }}
            devreDisi={!bas || !bit}
          />
        }
      />

      {/* AK-23 — ayrım ekranda da AÇIKÇA anlatılır */}
      <Uyari tur="bilgi" baslik="Zamanında iade ile elde bozulan mal AYRIDIR">
        <p className="m-0">
          <strong>Fire</strong> sütunu ELDE BOZULAN malı gösterir.{" "}
          <strong>Zamanında iade</strong> sütunu ise SKT dolmadan depoya geri
          gönderilen malı gösterir. Bu iki sütun <strong>toplanmaz</strong> ve
          zamanında iade fire'yi artırmaz — dürüst iade davranışı olumsuz metrik
          üretmemelidir.
        </p>
      </Uyari>

      <FiltreSeridi>
        <TarihAlani
          id="ns-bas"
          etiket="Başlangıç"
          deger={bas}
          degistir={(v) => {
            setDokunuldu(true);
            setBas(v);
          }}
        />
        <TarihAlani
          id="ns-bit"
          etiket="Bitiş"
          deger={bit}
          degistir={(v) => {
            setDokunuldu(true);
            setBit(v);
          }}
          ipucu="Kaynak görünüm ay bazında gruplar; aralık aylara yuvarlanır."
        />
        {seciliNoktaId && (
          <Rozet tur="simit">nokta süzgeci sunucuda uygulanıyor</Rozet>
        )}
      </FiltreSeridi>

      <SorguDurumu
        yukleniyor={rapor.isLoading}
        hata={rapor.error}
        bos={!rapor.isLoading && satirlar.length === 0}
        bosBaslik="Bu dönemde nokta verisi yok"
        bosAciklama="Seçili aralıkta aktif nokta bulunamadı."
        iskeletSatir={6}
        yenile={() => void rapor.refetch()}
      >
        {rapor.data && (
          <>
            {rapor.data.ay_granulerligi && (
              <p className="m-0 text-sm text-murekkep-3" role="status">
                Rapor AY granülerliğindedir: {gunGoster(rapor.data.bas)} –{" "}
                {gunGoster(rapor.data.bit)} aralığı, ait olduğu ayların tamamını
                kapsar.
              </p>
            )}

            <Tablo>
              <TabloBaslik>
                <th scope="col">Nokta</th>
                <th scope="col">Sorumlu</th>
                <th scope="col">Fire (elde bozulan)</th>
                <th scope="col">Fire miktarı</th>
                <th scope="col">Zamanında iade</th>
                <th scope="col">Sayım farkı</th>
                <th scope="col">Transfer farkı</th>
                <th scope="col">İmha miktarı</th>
                <th scope="col">Satış</th>
                <th scope="col">İptal</th>
                <th scope="col">İptal oranı</th>
              </TabloBaslik>
              <TabloGovde>
                {satirlar.map((n) => (
                  <tr key={n.nokta_id}>
                    <th scope="row" className="px-3 py-2 text-left font-normal">
                      {n.nokta_ad}
                    </th>
                    <td>{n.sorumlu_ad ?? "—"}</td>
                    {/* Fire ve zamanında iade YAN YANA ama AYRI sütunlardır. */}
                    <td className="text-right">
                      <Tutar
                        deger={n.fire_tutari}
                        yonsuz
                        boy="sm"
                        etiket={`${n.nokta_ad} elde bozulan mal`}
                      />
                    </td>
                    <td className="rakam text-right font-mono">
                      {n.fire_miktari}
                    </td>
                    <td className="text-right">
                      <Tutar
                        deger={n.zamaninda_iade}
                        yonsuz
                        boy="sm"
                        etiket={`${n.nokta_ad} zamanında iade`}
                      />
                    </td>
                    <td className="text-right">
                      <Tutar deger={n.sayim_farki} isaretiGoster boy="sm" />
                    </td>
                    <td className="text-right">
                      <Tutar deger={n.transfer_farki} isaretiGoster boy="sm" />
                    </td>
                    <td className="rakam text-right font-mono">
                      {n.imha_miktari}
                    </td>
                    <td className="rakam text-right font-mono">
                      {n.satis_adedi}
                    </td>
                    <td className="rakam text-right font-mono">
                      {n.iptal_adedi}
                    </td>
                    <td className="rakam text-right font-mono">
                      {n.iptal_orani_yuzde ? `%${n.iptal_orani_yuzde}` : "—"}
                    </td>
                  </tr>
                ))}
              </TabloGovde>
            </Tablo>

            <p className="m-0 max-w-prose text-xs text-murekkep-3">
              {rapor.data.not}
            </p>
          </>
        )}
      </SorguDurumu>
    </Sayfa>
  );
}
